import React, { useEffect } from 'react'
import { ValidationDialogState, ValidationDialogOpenState } from './store/State'
import { useServiceState } from 'helpers'
import UserResponsesManagerService from 'smart-components/user-responses-manager/Service'
import ValidationDialogService from './Service'
import { get } from 'lodash'
import { MENU_LIST } from 'config/constants/navigation-menu'
import { navigate } from '@reach/router'
import { VALIDATION_MESSAGE } from 'config/constants/validation-messages'

export interface IValidationDialogViewProps extends ValidationDialogState {
  openDialog: any
  openInitiativeAlert: any
  getSectionByAttr: any
  getSectionById: any
  navigateToSection: any
  navigateToWarningSection: any
  getErrorSections: any
  getWarningFields: any
  getQuestionTitle: any
  getQuestionDescription: any
  getSectionErrorDescription: any
  isVisible: any
}

export interface IValidationDialogProviderProps {
  children: (state: IValidationDialogViewProps) => React.ReactElement
}

const Provider: React.FC<IValidationDialogProviderProps> = ({ children }) => {
  const responsesState = useServiceState(UserResponsesManagerService, false)
  const validationDialogState = useServiceState(ValidationDialogService, false)

  const urlParams = new URLSearchParams(window.location.search)
  const displayErrors = urlParams.get('displayErrors')
  const displayAllQuestionsMustBeComplete = urlParams?.get('displayAllQuestionsMustBeComplete')

  const openDialog = (status: ValidationDialogOpenState) => {
    ValidationDialogService.setIsDialogOpen(status)
  }

  const openInitiativeAlert = (status: ValidationDialogOpenState) => {
    ValidationDialogService.setIsInitiativeAlertOpen(status)
  }

  const getSectionByAttr = (attribute: string) => {
    return MENU_LIST.find(menu => menu?.validationFields?.includes(attribute))
  }

  const getSectionById = (id: string) => {
    return MENU_LIST.find(menu => menu.id === id)
  }

  const navigateToSection = (id: string, isMobileView: boolean) => {
    if (!isMobileView) {
      navigate(getSectionById(id)?.path)
    } else {
      window.location.hash = '#'.concat(id)
    }
    ValidationDialogService.setIsDialogOpen('CLOSED')
  }

  const navigateToWarningSection = (attribute: string, isMobileView: boolean) => {
    navigateToSection(getSectionByAttr(attribute)?.id, isMobileView)
  }

  const getErrorSections = () => {
    return (
      MENU_LIST.filter(
        section =>
          section.validation &&
          validationDialogState.validation.total &&
          !validationDialogState.validation.total[section.validation]
      ) || []
    )
  }

  const getWarningFields = () => {
    const questions = validationDialogState.validation?.responses || VALIDATION_MESSAGE.QUESTION || {}
    return Object.keys(questions)?.filter(attr => !questions[attr]?.valid) || []
  }

  const getQuestionTitle = (attribute: string) => {
    return VALIDATION_MESSAGE.QUESTION[attribute]?.title
  }

  const getQuestionDescription = (attribute: string) => {
    return VALIDATION_MESSAGE.QUESTION[attribute]?.description || VALIDATION_MESSAGE.DEFAULT_QUESTION_DESCRIPTION
  }

  const getSectionErrorDescription = (id: string) => {
    return VALIDATION_MESSAGE.SECTION[id]
  }

  const isVisible = () => {
    return validationDialogState.validation?.total && (getErrorSections().length > 0 || getWarningFields().length > 0)
  }

  const viewMethods = {
    openDialog,
    openInitiativeAlert,
    getSectionByAttr,
    getSectionById,
    navigateToSection,
    navigateToWarningSection,
    getErrorSections,
    getWarningFields,
    getQuestionTitle,
    getQuestionDescription,
    getSectionErrorDescription,
    isVisible
  }

  useEffect(() => {
    const validation = get(responsesState, 'userResponses.validation', null)
    if (validation && validationDialogState?.validation?.responses !== validation?.responses) {
      ValidationDialogService.setValidationOptions(validation)
    }
    if (
      displayAllQuestionsMustBeComplete === 'true' &&
      validationDialogState.isInitiativeAlertState === 'UNDEFINED' &&
      validation &&
      !validation?.total?.isComplete
    ) {
      ValidationDialogService.setIsInitiativeAlertOpen('OPEN')
    }
  }, [responsesState, validationDialogState, displayAllQuestionsMustBeComplete])

  useEffect(() => {
    if (displayErrors === 'true') {
      ValidationDialogService.setIsDialogOpen('OPEN')
    }
  }, [displayErrors])

  return <React.Fragment>{children({ ...validationDialogState, ...viewMethods })}</React.Fragment>
}

export default Provider
